import type { Money } from "../types/money-brand";
import { formatMoney } from "./money";

export type TournamentDisplayStatus =
  | "upcoming"
  | "in_progress"
  | "completed"
  | "cancelled";

export type TournamentRegistrationState =
  | "unset"
  | "scheduled"
  | "open"
  | "closed";

const TOURNAMENT_STATUS_LABELS: Record<TournamentDisplayStatus, string> = {
  upcoming: "Upcoming",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export function normalizeTournamentStatus(
  status: string | null | undefined,
): TournamentDisplayStatus {
  switch (status) {
    case "active":
    case "in_progress":
      return "in_progress";
    case "completed":
      return "completed";
    case "cancelled":
      return "cancelled";
    case "draft":
    case "open":
    case "upcoming":
    default:
      return "upcoming";
  }
}

export function getTournamentStatusLabel(
  status: string | null | undefined,
): string {
  return TOURNAMENT_STATUS_LABELS[normalizeTournamentStatus(status)];
}

/**
 * Canonical date-range string for a tournament (the single PUBLIC impl,
 * re-exported from `@bw/shared/utils` via index.ts).
 *
 * Tournament start/end are conceptually DATE-ONLY: the business date-picker
 * submits a midnight-UTC value (`2026-07-04T00:00:00Z`). The displayed day MUST
 * equal the stored calendar day for every viewer, so formatting is UTC-anchored
 * — every `toLocaleDateString` call passes `timeZone: "UTC"` and every calendar
 * comparison uses `getUTC*()`. Without this a negative-UTC viewer (e.g. Pacific)
 * renders the prior day ("Jul 3" for a "Jul 4" tournament).
 *
 * Rules:
 * - No end date → "Jul 4, 2026"
 * - Same UTC calendar day → "Jul 4, 2026" (collapsed, never "Jul 4 – 4, 2026")
 * - Same UTC month → "Jul 4 – 6, 2026" (abbreviated end day, year once)
 * - Same UTC year, different month → "Apr 24 – May 7, 2026"
 * - Cross-year → "Dec 30, 2026 – Jan 3, 2027"
 *
 * The separator is an en-dash (`–`, U+2013) — the typographically correct range
 * separator. Hyphen-minus (`-`) is for compound words and was the prior bug.
 */
export function formatTournamentDateRange(
  startDate: string,
  endDate?: string | null,
  locale: string = "en-US",
): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;

  const formatDay = (value: Date) =>
    value.toLocaleDateString(locale, {
      timeZone: "UTC",
      day: "numeric",
    });
  const formatMonthDay = (value: Date) =>
    value.toLocaleDateString(locale, {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
  const fullDate = (value: Date) =>
    value.toLocaleDateString(locale, {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const formatYear = (value: Date) =>
    value.toLocaleDateString(locale, {
      timeZone: "UTC",
      year: "numeric",
    });

  if (!end) {
    return fullDate(start);
  }

  const sameDay =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCDate() === end.getUTCDate();
  if (sameDay) {
    return fullDate(start);
  }

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();

  if (sameMonth) {
    return `${formatMonthDay(start)} – ${formatDay(end)}, ${formatYear(end)}`;
  }

  if (sameYear) {
    return `${formatMonthDay(start)} – ${formatMonthDay(end)}, ${formatYear(end)}`;
  }

  return `${fullDate(start)} – ${fullDate(end)}`;
}

function formatTournamentRegistrationDate(
  value: string,
  locale: string,
): string {
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function getTournamentRegistrationState(
  registrationOpenAt?: string | null,
  registrationCloseAt?: string | null,
  now: Date = new Date(),
): TournamentRegistrationState {
  if (!registrationOpenAt || !registrationCloseAt) {
    return "unset";
  }

  const opensAt = new Date(registrationOpenAt);
  const closesAt = new Date(registrationCloseAt);
  if (now < opensAt) {
    return "scheduled";
  }
  if (now > closesAt) {
    return "closed";
  }
  return "open";
}

export function getTournamentRegistrationLabel(
  registrationOpenAt?: string | null,
  registrationCloseAt?: string | null,
  locale: string = "en-US",
  now: Date = new Date(),
): string {
  const state = getTournamentRegistrationState(
    registrationOpenAt,
    registrationCloseAt,
    now,
  );

  if (state === "unset") {
    return "Registration details coming soon";
  }
  if (state === "scheduled" && registrationOpenAt) {
    return `Opens on ${formatTournamentRegistrationDate(registrationOpenAt, locale)}`;
  }
  if (state === "closed" && registrationCloseAt) {
    return `Closed on ${formatTournamentRegistrationDate(registrationCloseAt, locale)}`;
  }
  if (registrationCloseAt) {
    return `Open until ${formatTournamentRegistrationDate(registrationCloseAt, locale)}`;
  }
  return "Registration details coming soon";
}

// Structural view of the generated TournamentRegistrationCommerceSummary, just
// the fields this pure label helper reads. `entry_fee_from` carries the branded
// Money type (ADR-005) so it matches the generated schema and can't be redeclared
// as a plain number/string (the no-money-arithmetic guardrail); it is rendered via
// formatMoney, never raw arithmetic. Callers pass the generated summary directly.
export interface TournamentRegistrationCommerceLike {
  event_count: number;
  paid_event_count: number;
  free_event_count: number;
  entry_fee_from?: Money | null;
  currency?: string | null;
  has_mixed_currencies?: boolean;
}

function formatTournamentSummaryDate(
  value: string,
  locale: string,
): string {
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
}

export function getTournamentCommerceLabel(
  registrationCommerce?: TournamentRegistrationCommerceLike | null,
): string {
  if (!registrationCommerce || registrationCommerce.event_count <= 0) {
    return "Events coming soon";
  }

  const countLabel = `${registrationCommerce.event_count} event${
    registrationCommerce.event_count === 1 ? "" : "s"
  }`;

  if (registrationCommerce.paid_event_count <= 0) {
    return `${countLabel} · Free to enter`;
  }

  const feeLabel =
    !registrationCommerce.has_mixed_currencies &&
    registrationCommerce.entry_fee_from != null &&
    registrationCommerce.currency
      ? formatMoney(
          registrationCommerce.entry_fee_from,
          registrationCommerce.currency,
        )
      : null;

  if (registrationCommerce.free_event_count <= 0) {
    return feeLabel ? `${countLabel} · From ${feeLabel}` : `${countLabel} · Paid events`;
  }

  return feeLabel
    ? `${countLabel} · Free and paid from ${feeLabel}`
    : `${countLabel} · Free and paid events`;
}

export function getTournamentRefundSummaryLabel(
  refundDeadlineAt?: string | null,
  locale: string = "en-US",
  now: Date = new Date(),
): string {
  if (!refundDeadlineAt) {
    return "No automatic refund cutoff";
  }

  const deadline = new Date(refundDeadlineAt);
  const dateLabel = formatTournamentSummaryDate(refundDeadlineAt, locale);
  if (deadline.getTime() < now.getTime()) {
    return `Automatic refunds ended ${dateLabel}`;
  }
  return `Automatic refunds through ${dateLabel}`;
}
